import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useApiClient } from './useApiClient.js'

// Declarative GET hook — the replacement for the legacy data-fetching hook
// (plan §2 D6).
//
//   const { data, loading, error, refetch } = useApi('/api/v1/products', { page: 1 })
//
// Same shape as the legacy hook:
//   data    — response body (null until loaded)
//   loading — true during the initial fetch (false when `enabled` is false,
//             matching the legacy hook; background refetches do not re-set it)
//   error   — ApiError | null (was a message string under the legacy hook; use
//             error.message / error.code — Phase 5 call sites adopt this)
//   refetch — imperative re-run
//
// options:
//   enabled   — false to hold the query (default true), like the legacy hook
//   onSuccess — called with data after every successful fetch (legacy parity)
//   query     — escape hatch: extra options merged into useQuery
//
// Query key is [path, params] — invalidate with
// queryClient.invalidateQueries({ queryKey: [path] }) or the exact pair.
export function useApi(path, params, options = {}) {
  const client = useApiClient()
  const enabled = options.enabled !== false

  const query = useQuery({
    queryKey: [path, params ?? null],
    queryFn: ({ signal }) => client.get(path, params, { signal }),
    enabled,
    ...(options.query || {}),
  })

  const onSuccessRef = useRef(options.onSuccess)
  useEffect(() => {
    onSuccessRef.current = options.onSuccess
  })
  const { data, dataUpdatedAt } = query
  useEffect(() => {
    if (data !== undefined && dataUpdatedAt > 0) onSuccessRef.current?.(data)
  }, [data, dataUpdatedAt])

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
  }
}
